/* Copyright (c) 2016-2023 Planticle Pty Ltd */
"use strict";

import ko from "knockout";
import birdata from "../../api.js";
import programs from "../../programs.js";
import util from "../../util/util.js";
import moment from "dayjs";
import DrawPolygonTool from "../../util/draw-polygon-tool.js";

const forceProgramCode = null;

const DataFiltersViewModel = function(params) {
	const self = this;

	let initialState = { ...util.queryParams(), ...util.hashParams() };

	self.loaded = ko.observable(false);

	self.speciesFilter = params.speciesFilter;
	self.multipleSpeciesFilter = params.multipleSpeciesFilter;
	self.regionFilter = params.regionFilter;
	self.programFilter = params.programFilter;

	self.areaLayerFilter = params.areaLayerFilter;
	self.areaFilter = params.areaFilter;
	self.visibleOnMapFilter = params.visibleOnMapFilter;

	self.sourceFilter = params.sourceFilter;

	self.afterDateFilter = params.afterDateFilter;
	self.beforeDateFilter = params.beforeDateFilter;
	self.monthsFilter = params.monthsFilter;

	self.dateFilters = !!(self.afterDateFilter && self.beforeDateFilter);

	self.polygonFilter = params.polygonFilter;
	self.map = params.map;
	self.showPolygonFilter = !!self.polygonFilter;

	self.userFilter = params.userFilter;

	self.minCountFilter = params.minCountFilter?.extend({ rateLimit: { timeout: 500, method: "notifyWhenChangesStop"}});
	self.maxCountFilter = params.maxCountFilter?.extend({ rateLimit: { timeout: 500, method: "notifyWhenChangesStop"}});
	self.countFilters = !!(self.minCountFilter && self.maxCountFilter);

	self.lockedFilters = ko.observable([]);

	self.isLocked = function(filter) {
		return self.lockedFilters().includes(filter);
	}

	if(self.polygonFilter && self.map) {
		var drawPolygonTool = new DrawPolygonTool();
		drawPolygonTool.polygon = self.polygonFilter;
		self.map.subscribe(function(map) {
			drawPolygonTool.map = map;
		});
		drawPolygonTool.map = self.map();
		self.isDrawingPolygon = drawPolygonTool.isDrawingPolygon;
		self.drawPolygon = function() { drawPolygonTool.drawPolygon(); }
		self.cancelPolygon = function() { drawPolygonTool.cancelPolygon(); }
	}

	if(self.monthsFilter) {
		self.months = util.monthsShort().map(function(m, i) {
			return {
				month: m,
				index: i + 1
			};
		});
		self.allMonthsFilter = ko.pureComputed({
			read: function() {
				return self.monthsFilter().length === self.months.length;
			},
			write: function(value) {
				self.monthsFilter(value ? self.months.map(x => x.index) : []);
			}
		});
		self.allMonthsFilter(true);
		self.monthsFilterDelayed = ko.pureComputed(function() {
			return self.monthsFilter();
		}).extend({ rateLimit: { timeout: 1500, method: "notifyWhenChangesStop"}});
	}

	if(self.userFilter) {
		self.usersMatchingSearch = ko.observable([]);
		self.usersSearchText = ko.observable('').extend({ rateLimit: 400 });

		self.usersSearchText.subscribe(function(search) {
			updateUsers(search, self.usersSearchText, self.usersMatchingSearch);
		});

		function updateUsers(search, searchObs, matchesObs) {
			if(search) {
				birdata.users({q:search, full: false, limit: 20}).then(function(users) {
					if(searchObs() != search) return; // ignore stale request
					//users = users.filter(u => u.full_name);
					matchesObs(users);
				});
			} else {
				matchesObs([]);
			}
		}

		self.userOptionText = function(user) {
			var text = user.full_name || "";
			if(user.email) {
				text = text + " <" + user.email + ">";
			}
			return text;
		};
	}


	self.criteria = ko.pureComputed(function() {
		if(!self.loaded()) {
			return null;
		}
		let criteria = {};

		if(self.regionFilter()) {
			criteria.region_id = self.regionFilter().id;
		}

		if(self.programFilter()) {
			criteria.program = self.programFilter().code;
		}

		if(self.areaFilter()) {
			criteria.area_id = self.areaFilter().id;
		}

		if(self.areaLayerFilter()) {
			criteria.area_layer_id = self.areaLayerFilter().id;
		}

		if(self.speciesFilter?.()) {
			criteria.species_id = self.speciesFilter().id;
		}
		if(self.multipleSpeciesFilter?.()) {
			criteria.species_ids = self.multipleSpeciesFilter().map(s => s.id).join(",");
		}

		if(self.visibleOnMapFilter() && params.mapBounds) {
			let bounds = params.mapBounds();
			if(bounds) {
				criteria.xmin = bounds.getSouthWest().lng();
				criteria.ymin = bounds.getSouthWest().lat();
				criteria.xmax = bounds.getNorthEast().lng();
				criteria.ymax = bounds.getNorthEast().lat();
			}
		}

		if(self.sourceFilter()) {
			criteria.source = self.sourceFilter().code;
		}
		if(self.afterDateFilter()) {
			criteria.from = moment(self.afterDateFilter()).format("YYYY-MM-DD");
		}
		if(self.beforeDateFilter()) {
			criteria.to = moment(self.beforeDateFilter()).format("YYYY-MM-DD");
		}
		if(self.monthsFilterDelayed()) {
			if(self.monthsFilterDelayed().length < 12) {
				criteria.months = self.monthsFilterDelayed().toSorted().join(",");
			}
		}
		if(self.polygonFilter()) {
			criteria.polygon = drawPolygonTool.base64EncodedPolygon;
		}
		if(self.userFilter?.()) {
			criteria.user_id = self.userFilter().id;
		}
		if(self.minCountFilter?.()) {
			criteria.min_individual_count = self.minCountFilter();
		}
		if(self.maxCountFilter?.()) {
			criteria.max_individual_count = self.maxCountFilter();
		}

		criteria.permit_stale = true;

		return criteria;
	});

	self.criteria.subscribe(function(c) {
		params.criteria(c)
	});

	// self.subspeciesFilter = params.subspeciesFilter;// ko.observable(null);
	// self.guildFilter = params.guildFilter;// ko.observableArray();
	// self.classFilter = params.classFilter;// ko.observable(null);
	// self.userFilter = params.userFilter;// ko.observable(null);
	// self.polygonFilter = params.polygonFilter;// ko.observable(null);

	const loadPromises = [];
	const mePromise = birdata.me();

	if(self.speciesFilter || self.multipleSpeciesFilter) {
		self.species = ko.observable([]);
		self.selectableSpecies = ko.pureComputed(() => {
			if(self.programFilter && self.programFilter() && self.programFilter().code == "sop") {
				return self.species();
			} else {
				return self.species().filter(s => s.supertaxa.includes("class:Aves"));
			}
		});
		loadPromises.push(birdata.speciesList({ supertaxa: "all" }).then(self.species));
	}

	if(self.regionFilter) {
		self.regions = ko.observable([]);
		loadPromises.push(birdata.regions().then(self.regions));
	}

	if(self.programFilter) {
		self.programs = ko.observable([]);
		loadPromises.push(
			Promise.all([birdata.lookup('program'), programs(), mePromise])
			.then(filterPrograms)
			.then(self.programs));
	}

	if(self.areaFilter && self.areaLayerFilter) {
		self.areaLayers = ko.observable([]);
		self.areas = ko.observable([]);

		loadPromises.push(birdata.areaLayers().then(self.areaLayers));

		let areasPromise = birdata.areas().then(areas => self.allAreas = areas);
		self.areasPromise = areasPromise;

		if(initialState.area_id) {
			loadPromises.push(areasPromise);
		}

		self.areaLayerFilter.subscribe(function(areaLayer) {
			areasPromise.then(function(areas) {
				self.areas(filterAreas(areas, areaLayer));
			});
			if(!areaLayer && self.areaFilter()) {
				self.areaFilter(null)
			}
		});
	}

	if(self.sourceFilter) {
		self.sources = ko.observable([]);
		loadPromises.push(
			birdata.lookup('source').then(self.sources));
	}

	self.speciesMatchFn = function(option, text, search) {
		text = text.toLowerCase().replace("-", " ");
		search = search.toLowerCase().replace("-", " ");
		return text.indexOf(search);
	};

	Promise.allSettled(loadPromises).then(function() {
		applyInitialState();
		if(params.initFn) {
			params.initFn();
		}
		self.loaded(true)
	});

	function speciesById(id) {
		return self.species().find(species => species.id === +id);
	}

	function applyInitialState() {
		if(initialState.species_id) {
			self.speciesFilter(speciesById(initialState.species_id));
		}
		if(initialState.species_ids) {
			self.multipleSpeciesFilter(initialState.species_ids.split(",").map(speciesById))
		}
		// if(initialState.taxon_id) {
		// 	self.subspeciesFilter(_.findWhere(self.speciesFilter(), {taxon_id: initialState.taxon_id}));
		// }
		if(initialState.region_id) {
			self.regionFilter(self.regions().find(region => region.id === +initialState.region_id));
		}
		if(forceProgramCode) {
			initialState.program = forceProgramCode;
		}
		if(initialState.program) {
			self.programFilter(self.programs().find(program => program.code === initialState.program));
		}
		if(initialState.visible_on_map) {
			self.visibleOnMapFilter(initialState.visible_on_map == "true");
		}
		if(initialState.area_layer_id) {
			self.areaLayerFilter(self.areaLayers().find(area => area.id === +initialState.area_layer_id));
		}
		if(initialState.area_id) {
			self.areaFilter(self.allAreas.find(a => a.id === +initialState.area_id));
		}
		if(initialState.polygon) {
			drawPolygonTool.base64EncodedPolygon = initialState.polygon;
		}
		if(initialState.from) {
			self.afterDateFilter(initialState.from);
		}
		if(initialState.to) {
			self.beforeDateFilter(initialState.to);
		}
		if(initialState.months) {
			var m = initialState.months.split(/[,_]/).map(i => +i).filter(i => !isNaN(i));
			self.monthsFilter(m);
		}
		if(initialState.source) {
			self.sourceFilter(self.sources().find(source => source.code === initialState.source));
		}
		if(initialState.user_id) {
			var userID = +initialState.user_id;

			self.userFilter({
				id: userID,
				full_name: "User " + userID
			});
			// Populate select with user's name:
			birdata.user(userID, { full: true }).then(function(user) {
				self.userFilter(user);
			});
		}
		if(initialState.min_individual_count) {
			self.minCountFilter(+initialState.min_individual_count);
		}
		if(initialState.max_individual_count) {
			self.maxCountFilter(+initialState.max_individual_count);
		}

		// if(initialState.program == 'wa_cockies' && initialState.wa_cockies_part_of_gcc) {
		// 	self.gccFilter(initialState.gcc == 'true');
		// }
		params.haveAppliedInitialState = true;
	}

	params.lockedFilters = self.lockedFilters;

	params.selectArea = function(areaId) {
		self.areasPromise.then(function() {
			let area = self.allAreas.find(a => a.id === areaId);
			if(area) {
				self.areaLayerFilter(self.areaLayers().find(layer => layer.id === area.layer_id));
				self.areaFilter(area);
			}
		});
	};

	params.selectProgram = function(programCode) {
		self.programFilter(self.programs().find(x => x.code === programCode));
	}

	params.selectSpecies = function(speciesId) {
		let species = self.species().find(sp => sp.id === speciesId);
		if(self.speciesFilter) {
			self.speciesFilter(species);
		}
		if(self.multipleSpeciesFilter) {
			self.multipleSpeciesFilter([species]);
		}
	}

	params.selectRegion = function(regionId) {
		self.regionFilter(self.regions().find(x => x.id === regionId));
	}
};

function filterPrograms([programs, programLogic, me]) {
	return programs.filter(function(p) {
		if(forceProgramCode && p.code == forceProgramCode) {
			return true;
		}

		let pl = programLogic.find(p.code);
		if(pl && pl.canFilterBy) {
			return pl.canFilterBy(me);
		} else {
			return true;
		}
	});
}

function filterAreas(areas, areaLayer) {
	if(!areaLayer) {
		return [];
	} else {
		return areas
			.filter(area => area.layer_id == areaLayer.id)
			.toSorted((a, b) => a.name.localeCompare(b.name));
	}
}

ko.components.register('data-filters', {
	viewModel: function(params) {
		return new DataFiltersViewModel(params);
	},
	template: require('../../../templates/shared/data-filters.html')
});

export default DataFiltersViewModel;
